import type { LeDataLensCardSuggestion } from '~/types/leData';

/** A dashboard the signed-in person is already allowed to open. */
export type DashboardDestination = {
  lensId: string;
  title: string;
  cardTitles: ReadonlyArray<string>;
};

export type DashboardDestinations = {
  /** The dashboard surrounding this conversation. This is always the clearest next place to go. */
  current?: DashboardDestination;
  /** Certified dashboards are candidates only when the answer matches one unambiguously. */
  certified?: ReadonlyArray<DashboardDestination>;
  onOpenDashboard?: (lensId: string) => void;
};

export type MatchedDashboard = {
  dashboard: DashboardDestination;
  source: 'current' | 'certified';
};

function normalise(value: string): string {
  return value
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

/**
 * Finds a dashboard that is safe to name beside an answer. The current dashboard wins; otherwise
 * a certified dashboard must be the *only* exact title match. We deliberately do not use fuzzy
 * matching here: sending someone to the wrong dashboard is worse than offering the neutral save
 * action, and the model has not yet been given dashboard definitions as an input.
 */
export function matchDashboardForSuggestion(
  suggestion: LeDataLensCardSuggestion | undefined,
  destinations: DashboardDestinations | undefined,
): MatchedDashboard | undefined {
  if (destinations?.current) {
    return { dashboard: destinations.current, source: 'current' };
  }

  const candidateTitle = normalise(suggestion?.title ?? '');
  if (!candidateTitle) return undefined;

  const matches = (destinations?.certified ?? []).filter((dashboard) => {
    return (
      normalise(dashboard.title) === candidateTitle ||
      dashboard.cardTitles.some((cardTitle) => normalise(cardTitle) === candidateTitle)
    );
  });

  return matches.length === 1 ? { dashboard: matches[0], source: 'certified' } : undefined;
}
